import { HttpClient } from '@/data/protocols/http'
import { AccountPersonalProfileModel } from '@/domain/models/account/account-personal-profile-model'
import { AddressStateBrazil } from '@/domain/models/address/address-state-brazil'
import { Category } from '@/domain/models/content/category'
import { CountryCode } from '@/domain/models/country-code'
import { DocumentModel, DocumentResponse } from '@/domain/models/document/document-model'
import IdNameModel from '@/domain/models/id-name-model'
import { Account } from '@/domain/usecases'
import { AccountBenefit } from '@/domain/usecases/account/account-benefit'
import { GenericResponse } from '@/infra/http/generic-response'
import { QueryClient } from '@tanstack/react-query'
import { BaseRemote } from '../base-remote'
import PatternRegex from '../data-format-pattern/pattern-regex'
import { AccountRole } from './account-role'
import { AccountType } from './account-type'

export interface AccountResponse {
  id: string
  accountType: string
  name: string
  socialName?: string
  email: string
  document: DocumentResponse
  pictureUrl?: string
  onboardingCompleted: boolean
  onboardingCompletedAt: string | null
  roles: string[]
  passPlan?: {
    id: number
    name: string
  } | null
  addons?: {
    refund: {
      enabled: boolean
    }
  } | null
  company: {
    id: number
    uniqueId: string
    name: string
    logoUrl?: string | null
    politicsUrl?: string | null
    singleSignOnOptions: {
      providerName: string
      required: boolean
    }[]
    contentRestrictions: {
      categories: string[]
    }
    requiresApproval: boolean
    allowUpgrade: boolean
    allowDependents: boolean
    emailValidationRequired: boolean
    phoneValidationRequired: boolean
  }
  preferenceLanguage: string
}

interface AddressResponseModel {
  zipCode?: string
  street?: string
  number?: string
  complement?: string
  district?: string
  city?: string
  state?: {
    id: number
    name: string
  }
  country?: string
}

export interface AccountProfileResponse extends AccountResponse {
  personalEmail?: string
  registration?: DocumentResponse
  birthDate?: string
  phone?: string
  address?: AddressResponseModel
  department?: IdNameModel
  job?: IdNameModel
}

export class RemoteAccountProfile extends BaseRemote implements Account {
  constructor(
    protected readonly queryClient: QueryClient,
    protected readonly url: string,
    protected readonly httpClient: HttpClient<RemoteAccountProfile.Model>
  ) {
    super()
  }

  async loader(): Promise<Account.ProfileModel> {
    return this.queryClient.fetchQuery({
      queryKey: [Account.ProfileKey],
      queryFn: () => this.profile(),
      staleTime: 1000
    })
  }

  async profile(): Promise<Account.ProfileModel> {
    const response = await this.httpClient.request({
      url: `${this.url}/profile`,
      method: 'get'
    })

    return this.onSuccessData(response, body => {
      return this.parseAccountProfile(body as GenericResponse<RemoteAccountProfile.ProfileModel>)
    })
  }

  async updateProfile(accountProfile: Account.ProfileModel): Promise<GenericResponse<void>> {
    const response = await this.httpClient.request({
      url: `${this.url}/profile`,
      method: 'put',
      body: this.parsePersonalProfile(accountProfile)
    })

    return this.onSuccess(response)
  }

  async loaderGet(): Promise<Account.ProfileModel> {
    return this.queryClient.fetchQuery({
      queryKey: [Account.Key],
      queryFn: () => this.get(),
      staleTime: 1000
    })
  }

  async get(): Promise<Account.Model> {
    const response = await this.httpClient.request({
      url: this.url,
      method: 'get'
    })

    return this.onSuccessData(response, body => {
      return RemoteAccountProfile.parseAccount(
        body as GenericResponse<RemoteAccountProfile.BaseModel>
      )
    })
  }

  public static parseAccount(
    response: GenericResponse<RemoteAccountProfile.BaseModel>
  ): Account.Model {
    const { accountType, roles, company } = response.data!
    const onboardingCompletedAt = response.data!.onboardingCompletedAt
      ? new Date(response.data!.onboardingCompletedAt)
      : null
    return {
      ...response.data!,
      document: DocumentModel.create(response.data!.document),
      accountType: AccountType.findByName(accountType)!,
      onboardingCompletedAt: onboardingCompletedAt,
      company: {
        ...company,
        contentRestrictions: {
          categories: company.contentRestrictions.categories.map(category =>
            Category.getByName(category)
          )
        }
      },
      roles: roles
        ? roles
            .filter(role => role != null)
            .map(role => AccountRole.findByName(role) as AccountRole.Enum)
        : [],
      preferenceLanguage: response.data!.preferenceLanguage
    }
  }

  protected parseAccountProfile(
    response: GenericResponse<RemoteAccountProfile.ProfileModel>
  ): Account.ProfileModel {
    const data = response.data!
    const country = CountryCode.getByValue(data.address?.country) ?? CountryCode.Enum.BR
    const state =
      country === CountryCode.Enum.BR
        ? AddressStateBrazil.getByValue(data.address?.state?.name)?.getTitle()
        : data.address?.state?.name
    return {
      ...data,
      ...RemoteAccountProfile.parseAccount(response),
      registration: DocumentModel.create(data.registration!),
      address: data.address
        ? {
            ...data.address,
            state: state ?? '',
            country: country
          }
        : undefined
    }
  }

  protected parsePersonalProfile(accountProfile: AccountPersonalProfileModel) {
    const country = accountProfile.address?.country
    const stateBR = AddressStateBrazil.getByValue(accountProfile.address?.state)
    let state = null
    if (country === CountryCode.Enum.BR) {
      state = stateBR && {
        id: stateBR?.id ?? 0,
        name: stateBR?.getTitle() ?? ''
      }
    } else {
      state = accountProfile.address?.state
        ? {
            id: 0,
            name: accountProfile.address?.state
          }
        : null
    }
    const zipCode =
      accountProfile.address?.country === CountryCode.Enum.BR
        ? PatternRegex.getOnlyNumbers(accountProfile.address?.zipCode)
        : accountProfile.address?.zipCode
    return {
      ...accountProfile,
      phone: accountProfile.phone,
      registration: {
        type: accountProfile.registration?.type.name,
        value: accountProfile.registration?.value
      },
      document: {
        type: accountProfile.document?.type.name,
        value:
          accountProfile.document?.type
            ?.getDynamicPattern()
            .normalize(accountProfile.document!.value!) ?? ''
      },
      address: {
        ...accountProfile.address,
        zipCode,
        state: state,
        country: accountProfile.address?.country?.name
      }
    }
  }

  async updatePersonalProfile(
    accountProfile: Account.PersonalProfileModel
  ): Promise<GenericResponse<void>> {
    const accountResp = await this.httpClient.request({
      url: `${this.url}/profile`,
      method: 'put',
      body: this.parsePersonalProfile(accountProfile)
    })
    return this.onSuccess(accountResp)
  }

  async updatePicture(accountId: string, file: File): Promise<Account.PictureModel> {
    const formData = new FormData()
    formData.append('file', file)

    const pictureResp = await this.httpClient.request({
      url: `${this.url}/${accountId}/profile/picture`,
      method: 'patch',
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    const successResp = this.onSuccessData(pictureResp) as GenericResponse<Account.PictureModel>
    return successResp.data as Account.PictureModel
  }

  async removePicture(accountId: string): Promise<void> {
    await this.httpClient.request({
      url: `${this.url}/${accountId}/profile/picture`,
      method: 'delete'
    })
  }

  async getBenefits(accountId: string): Promise<AccountBenefit.Model> {
    const benefitsResp = await this.httpClient.request({
      url: `${this.url}/${accountId}/benefits`,
      method: 'get'
    })
    const successResp = this.onSuccessData(benefitsResp) as GenericResponse<AccountBenefit.Model>
    return successResp.data as AccountBenefit.Model
  }
}

export namespace RemoteAccountProfile {
  export type Model = GenericResponse<AccountResponse | AccountProfileResponse | void>
  export type BaseModel = AccountResponse
  export type ProfileModel = AccountProfileResponse
}
